/**
 * Générateur — Utiliser les notions de multiples et de diviseurs.
 * Attendu ID : 699 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 6)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « vocabulaire » — Ex.1 Le bon vocabulaire
 *     Formats : texte_trou_drag (étiquettes multiple / diviseur / divisible),
 *     qcm_unique. Faits de tables (A3) : n = a × k. 4 gabarits dont le double
 *     « m est un multiple de a ET un diviseur de M » (chaîne a | m | M posée
 *     par construction).
 *
 *   Carte « vrai_faux » — Ex.3 Barre les phrases fausses (cœur du cadrage)
 *     Formats : tableau_vf, qcm_multiple (compte annoncé). Les 6 formulations
 *     croisées d'un couple (a, n = a·k) + le bloc conceptuel 0/1 en rotation.
 *
 *   Carte « division » — Ex.5 Division euclidienne et diviseurs (le pivot)
 *     Formats : texte_trou_saisie (a = b × q + r), qcm_unique (verdict AVEC
 *     justification — le reste nul, pas le quotient entier). Rotation
 *     divisible / non divisible par construction (r tiré).
 *
 *   Carte « communs » — Ex.10 Diviseurs communs
 *     Formats : texte_trou_saisie (liste des diviseurs « dans l'ordre
 *     croissant » — ADAPTATION validée : l'ordre imposé par la consigne rend
 *     les trous positionnels univoques), qcm_multiple (les diviseurs communs,
 *     compte annoncé), texte (le problème des bouquets → pgcd). Diviseurs
 *     CALCULÉS (pas de pool figé) sur des nombres composés variés.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 699;

const L_VOC = 'Multiple, diviseur ou divisible ?';
const L_VF  = 'Vrai ou faux ? (multiples et diviseurs)';
const L_DIV = 'Division euclidienne et diviseurs';
const L_COM = 'Diviseurs communs et bouquets';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOTS_VOC = ['multiple', 'diviseur', 'divisible'];
const MOTS_COMPTE = { 2: 'deux', 3: 'trois', 4: 'quatre', 5: 'cinq', 6: 'six' };

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

/** Diviseurs d'un entier, dans l'ordre croissant (calculés, pas de pool). */
const diviseursDe = (n) => {
    const res = [];
    for (let d = 1; d <= n; d++) if (n % d === 0) res.push(d);
    return res;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Le bon vocabulaire
// ═══════════════════════════════════════════════════════════

/** 4 gabarits — phrase à trous + version QCM (phrase, mot attendu). */
const tirerVocabulaire = () => {
    const a = randInt(3, 9);
    const k = randInt(3, 9);
    const n = a * k;
    const variante = randInt(0, 3);
    if (variante === 0) return { phrase: `${n} est un {{multiple}} de ${a}.`, enonce: `${n} est un … de ${a}.`, mot: 'multiple' };
    if (variante === 1) return { phrase: `${a} est un {{diviseur}} de ${n}.`, enonce: `${a} est un … de ${n}.`, mot: 'diviseur' };
    if (variante === 2) return { phrase: `${n} est {{divisible}} par ${a}.`, enonce: `${n} est … par ${a}.`, mot: 'divisible' };
    // Double : a | m | M par construction
    const k1 = randInt(2, 4);
    const k2 = randInt(2, 4);
    const m = a * k1;
    return {
        phrase: `${m} est un {{multiple}} de ${a} et un {{diviseur}} de ${m * k2}.`,
        enonce: null, mot: null,
    };
};

const genVocabulaireDrag = () => {
    const t = tirerVocabulaire();
    const distracteurs = filtrerDistracteurs(t.phrase, MOTS_VOC);
    const texte = '<p>Complète avec le bon vocabulaire :</p>';
    return makeTexteTrou(t.phrase, texte, { ...opts(L_VOC), mode: 'drag', distracteurs });
};

const genVocabulaireQcm = () => {
    let t = tirerVocabulaire();
    for (let i = 0; i < 10 && !t.mot; i++) t = tirerVocabulaire();   // variante simple requise
    if (!t.mot) t = { enonce: '36 est un … de 9.', mot: 'multiple' };
    const texte = `<p>Complète la phrase : <strong>« ${t.enonce} »</strong></p>`;
    return makeQcm([...MOTS_VOC], t.mot, texte, opts(L_VOC));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Vrai ou faux ?
// ═══════════════════════════════════════════════════════════

/** Les 6 formulations croisées d'un couple (a, n = a·k) + le bloc 0/1. */
const tirerPhrasesVF = () => {
    const a = randInt(3, 9);
    const k = randInt(2, 9);
    const n = a * k;
    const couple = [
        { txt: `${n} est un multiple de ${a}`, v: true },
        { txt: `${n} est un diviseur de ${a}`, v: false },
        { txt: `${n} est divisible par ${a}`, v: true },
        { txt: `${a} est un multiple de ${n}`, v: false },
        { txt: `${a} est un diviseur de ${n}`, v: true },
        { txt: `${a} est divisible par ${n}`, v: false },
    ];
    const conceptuel = [
        { txt: '0 est un multiple de tous les nombres entiers', v: true },
        { txt: '0 est un diviseur de tous les nombres entiers', v: false },
        { txt: '1 est un diviseur de tous les nombres entiers', v: true },
        { txt: '1 est un multiple de tous les nombres entiers', v: false },
    ];
    return { couple, conceptuel };
};

const genVFTableau = () => {
    const { couple, conceptuel } = tirerPhrasesVF();
    let lignes;
    for (let i = 0; i < 30; i++) {
        lignes = [...shuffle(couple).slice(0, 3), randomChoice(conceptuel)];
        const nbVrais = lignes.filter(l => l.v).length;
        if (nbVrais >= 1 && nbVrais < lignes.length) break;
    }
    const texte = '<p>Pour chaque phrase, indique si elle est vraie ou fausse.</p>';
    return makeVraiFaux(lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_VF));
};

const genVFQcmMulti = () => {
    const { couple, conceptuel } = tirerPhrasesVF();
    // Construction directe du mix : 3 vraies + 3 fausses tirées des pools
    const vraies = shuffle([...couple.filter(p => p.v), ...conceptuel.filter(p => p.v)]).slice(0, 3);
    const fausses = shuffle([...couple.filter(p => !p.v), ...conceptuel.filter(p => !p.v)]).slice(0, 3);
    const pool = shuffle([...vraies, ...fausses]);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[vraies.length]}</strong> phrases <strong>vraies</strong>.</p>`;
    return makeQcm(pool.map(p => p.txt), vraies.map(p => p.txt), texte, opts(L_VF), true);
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Division euclidienne et diviseurs
// ═══════════════════════════════════════════════════════════

const tirerDivision = () => {
    const b = randInt(7, 19);
    const q = randInt(23, 70);
    const divisible = randomChoice([true, false]);
    const r = divisible ? 0 : randInt(1, b - 1);
    return { a: b * q + r, b, q, r, divisible };
};

const genDivisionTrous = () => {
    const t = tirerDivision();
    const phrase = `${t.a} = ${t.b} × {{${t.q}}} + {{${t.r}}}`;
    const texte = `<p>Effectue la division euclidienne de <strong>${t.a}</strong> par ` +
        `<strong>${t.b}</strong>. Complète :</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L_DIV), mode: 'saisie' });
};

const genDivisionQcm = () => {
    const t = tirerDivision();
    const bonne = t.divisible
        ? 'Oui, car le reste de la division euclidienne est nul'
        : `Non, car le reste de la division euclidienne (${t.r}) n'est pas nul`;
    const pieges = [
        t.divisible
            ? 'Non, car le reste de la division euclidienne n\'est pas nul'
            : 'Oui, car le reste de la division euclidienne est nul',
        `Oui, car le quotient (${t.q}) est un nombre entier`,       // le quotient est TOUJOURS entier
        `Non, car ${t.a} est plus grand que ${t.b}`,                 // non pertinent
    ];
    const texte = `<p>On donne : <strong>${t.a} = ${t.b} × ${t.q} + ${t.r}</strong>.</p>` +
        `<p><strong>${t.b}</strong> est-il un diviseur de <strong>${t.a}</strong> ? ` +
        'Choisis la réponse accompagnée de la justification correcte.</p>';
    return makeQcm([bonne, ...pieges], bonne, texte, opts(L_DIV));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Diviseurs communs et bouquets
// ═══════════════════════════════════════════════════════════

/** Nombres composés « riches » (4 à 10 diviseurs) pour les listes et les couples. */
const NOMBRES_RICHES = [
    12, 16, 18, 20, 24, 27, 28, 30, 32, 36, 40, 42, 44, 45, 48,
    50, 52, 54, 56, 63, 64, 66, 68, 70, 75, 76, 78, 88, 92,
];

const genListeDiviseursTrous = () => {
    const n = randomChoice(NOMBRES_RICHES);
    const divs = diviseursDe(n);
    const phrase = `Diviseurs de ${n} : ${divs.map(d => `{{${d}}}`).join(', ')}`;
    const texte = `<p>Écris <strong>tous</strong> les diviseurs de <strong>${n}</strong>, ` +
        `<strong>dans l'ordre croissant</strong>. (Il y en a ${divs.length} — une case chacun.)</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L_COM), mode: 'saisie' });
};

const tirerCouple = () => {
    for (let i = 0; i < 40; i++) {
        const a = randomChoice(NOMBRES_RICHES);
        const b = randomChoice(NOMBRES_RICHES);
        if (a === b) continue;
        const communs = diviseursDe(pgcd(a, b));
        if (communs.length < 3 || communs.length > 6) continue;
        return { a, b, communs };
    }
    return { a: 18, b: 24, communs: [1, 2, 3, 6] };
};

const genCommunsQcmMulti = () => {
    const { a, b, communs } = tirerCouple();
    // Choix = communs + diviseurs d'un seul des deux (pièges), 8 max
    const union = [...new Set([...diviseursDe(a), ...diviseursDe(b)])];
    const pieges = shuffle(union.filter(d => !communs.includes(d))).slice(0, 8 - communs.length);
    const choix = [...communs, ...pieges].map(String);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[communs.length]}</strong> nombres qui sont ` +
        `des diviseurs communs de <strong>${a}</strong> et de <strong>${b}</strong>.</p>`;
    return makeQcm(choix, communs.map(String), texte, opts(L_COM), true);
};

const CTX_BOUQUETS = [
    { o1: 'roses', o2: 'tulipes', qui: 'La fleuriste', il: 'elle', quoi: 'bouquets' },
    { o1: 'billes rouges', o2: 'billes bleues', qui: 'Sami', il: 'il', quoi: 'sachets' },
    { o1: 'madeleines', o2: 'cookies', qui: 'Le pâtissier', il: 'il', quoi: 'boîtes' },
];

const genBouquetsSaisie = () => {
    const { a, b } = tirerCouple();
    const g = pgcd(a, b);
    const ctx = randomChoice(CTX_BOUQUETS);
    const Il = ctx.il.charAt(0).toUpperCase() + ctx.il.slice(1);
    const texte = `<p>${ctx.qui} a <strong>${a} ${ctx.o1}</strong> et <strong>${b} ${ctx.o2}</strong>. ` +
        `${Il} veut composer des ${ctx.quoi} <strong>tous identiques</strong>, en utilisant tout. ` +
        `Combien de ${ctx.quoi} peut-${ctx.il} composer, <strong>au maximum</strong> ?</p>`;
    return makeSaisie(String(g), texte, { ...opts(L_COM), suffixe: ctx.quoi });
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    vocabulaire_texte_trou_drag: genVocabulaireDrag,
    vocabulaire_qcm_unique:      genVocabulaireQcm,
    vrai_faux_tableau_vf:        genVFTableau,
    vrai_faux_qcm_multiple:      genVFQcmMulti,
    division_texte_trou_saisie:  genDivisionTrous,
    division_qcm_unique:         genDivisionQcm,
    communs_texte_trou_saisie:   genListeDiviseursTrous,
    communs_qcm_multiple:        genCommunsQcmMulti,
    communs_texte:               genBouquetsSaisie,
};

export const CATEGORIES = [
    {
        id: 'vocabulaire', label: L_VOC,
        formatsList: [
            { key: 'vocabulaire_texte_trou_drag', label: 'Drag' },
            { key: 'vocabulaire_qcm_unique',      label: 'QCM' },
        ],
    },
    {
        id: 'vrai_faux', label: L_VF,
        formatsList: [
            { key: 'vrai_faux_tableau_vf',   label: 'Vrai/Faux' },
            { key: 'vrai_faux_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'division', label: L_DIV,
        formatsList: [
            { key: 'division_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'division_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'communs', label: L_COM,
        formatsList: [
            { key: 'communs_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'communs_qcm_multiple',      label: 'QCM Multi' },
            { key: 'communs_texte',             label: 'Saisie' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    pgcd, diviseursDe, tirerVocabulaire, tirerPhrasesVF, tirerDivision, tirerCouple,
    NOMBRES_RICHES,
};
